import React, { useEffect, useState, useContext } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useHistory, useLocation } from 'react-router-dom'
import { useMutation } from '@apollo/client'
import { Formik, Field } from 'formik'
import * as Yup from 'yup'
import cn from 'classnames'
import { ModalContext } from 'modaaals'
import { SimpleLayout as Layout } from '../Layout'
import SelectionContext from '../../context/SelectionContext'

import { loggedInResponse } from '../../localStorage'
import { MUTATION, QUERY } from '../../apollo'
import * as Icon from '../Icons'

import styles from './login.module.css'

function Login() {
  let history = useHistory()
  let location = useLocation()
  const { openModal } = useContext(ModalContext)
  const { selectionState } = useContext(SelectionContext)
  const [errorMessage, setErrorMessage] = useState(null)
  const [responseMessage, setResponseMessage] = useState(null)
  const [returnTo, setReturnTo] = useState(null)
  const [isForgot, setIsForgot] = useState(false)

  const [login] = useMutation(MUTATION.LOGIN, {
    onError: (error) => {
      setErrorMessage(error.graphQLErrors[0].message)
    },
    awaitRefetchQueries: true,
    refetchQueries: [{ query: QUERY.CURRENT_USER }],
    update(cache, { data: { login } }) {
      const { token, refreshToken, user } = login
      if (token && refreshToken) {
        loggedInResponse(cache, { accessToken: token, refreshToken, user })
      }
    },
    onCompleted: () => {
      history.replace(returnTo || '/profile')
    },
  })

  const [forgotPassword] = useMutation(MUTATION.FORGOT_PASSWORD, {
    onError: (error) => {
      setErrorMessage(error.graphQLErrors[0].message)
    },
    onCompleted: ({ forgotPassword }) => {
      if (forgotPassword.ok) {
        setResponseMessage(forgotPassword.message)
      }
    },
  })

  useEffect(() => {
    if (location.state?.returnTo) {
      setReturnTo(location.state.returnTo)
      setErrorMessage('Please insert your credentials…')
    }
  }, [location.state])

  async function onSubmit({ email, password, magicLink }, { resetForm }) {
    try {
      if (isForgot) {
        await forgotPassword({ variables: { email, magicLink } })
      } else {
        await login({ variables: { email, password } })
      }
    } catch (err) {
      if (err === 'Not Found') {
        setErrorMessage('Email not found')
      }
      resetForm()
    }
  }

  function onFocus() {
    if (errorMessage) {
      setErrorMessage(null)
    }
  }

  return (
    <Layout className={styles.wrapper}>
      <div className={styles.innerWrapper}>
        <div
          className={styles.leftSide}
          style={{
            backgroundImage: `url(${require(`../../images/walkman.jpg`)})`,
          }}
        >
          <h3 className={cn(styles.maintitle, styles.leftTitle)}>
            The Definitive
            <br /> Musical Resource
          </h3>
        </div>
        <div className={styles.rightSide}>
          {responseMessage ? (
            <AnimatePresence>
              {responseMessage && (
                <motion.p
                  className={styles.form}
                  animate={{
                    opacity: 1,
                  }}
                  initial={{
                    opacity: 0,
                  }}
                  exit={{
                    opacity: 0,
                  }}
                  onAnimationComplete={() => {
                    setTimeout(() => setResponseMessage(null), 7000)
                  }}
                >
                  {responseMessage}
                </motion.p>
              )}
            </AnimatePresence>
          ) : (
            <Formik
              onSubmit={onSubmit}
              initialValues={{
                email: location.state?.email || '',
                ...(isForgot ? { password: '', magicLink: false } : {}),
              }}
              validationSchema={Yup.object().shape({
                email: Yup.string().email().required('Required'),
                ...(!isForgot
                  ? { password: Yup.string().required('Required') }
                  : {}),
              })}
            >
              {({ handleSubmit, errors, values }) => {
                return (
                  <form
                    className={styles.form}
                    onSubmit={handleSubmit}
                    autocomplete="new-password"
                  >
                    <h3 className={styles.maintitle}>
                      {isForgot ? 'Request Password' : 'Profile'}
                    </h3>
                    <label htmlFor="email" className={styles.label}>
                      Email<sup>*</sup>{' '}
                      <AnimatePresence>
                        {errorMessage && (
                          <motion.div
                            className={styles.inputError}
                            animate={{
                              opacity: 1,
                            }}
                            initial={{
                              opacity: 0,
                            }}
                            exit={{
                              opacity: 0,
                            }}
                          >
                            {errorMessage}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </label>
                    <input style={{ display: 'none' }} readOnly />
                    <Field
                      type="email"
                      name="email"
                      id="email"
                      className={cn(styles.input, {
                        [styles.error]: errors.email,
                      })}
                      placeholder="username@contact.com"
                      autocomplete="new-password"
                      onFocus={onFocus}
                    />
                    {isForgot && (
                      <>
                        <Field
                          type="checkbox"
                          id="magicLink"
                          name="magicLink"
                          className={styles.selectionCheckbox}
                        />
                        <label
                          htmlFor="magicLink"
                          className={styles.selectionLabelWrapper}
                        >
                          <div
                            className={cn(styles.selectionLabel, {
                              [styles.checked]: values.magicLink,
                            })}
                          >
                            <Icon.Check
                              className={styles.selectionLabelCheck}
                            />
                          </div>{' '}
                          Request a magic login link
                        </label>
                      </>
                    )}
                    {!isForgot && (
                      <>
                        <label htmlFor="password" className={styles.label}>
                          Password<sup>*</sup>
                        </label>
                        <Field
                          type="password"
                          name="password"
                          id="password"
                          className={cn(styles.input, {
                            [styles.error]: errors.password,
                          })}
                          placeholder="password"
                          onFocus={onFocus}
                          autocomplete="new-password"
                        />
                      </>
                    )}
                    <button type="submit" className={styles.button}>
                      {isForgot
                        ? values.magicLink
                          ? 'Send Magic Link'
                          : 'Request password'
                        : 'Login'}
                    </button>
                    <p className={styles.forgotButtonCont}>
                      {isForgot ? 'Go back to ' : ''}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault()
                          setIsForgot((v) => !v)
                        }}
                        className={styles.forgotButton}
                      >
                        {isForgot ? 'Login' : 'Request Password or Magic Link'}
                      </button>
                    </p>
                    <p>
                      Login with your email to access your personalized music
                      sync dashboard with music selection, project information
                      and quote management.
                    </p>
                    <p
                      style={{
                        lineHeight: 2.5,
                        marginTop: -5,
                      }}
                    >
                      To create a profile account please send us a <br />
                      <button
                        type="button"
                        className={styles.extraButton}
                        onClick={() => {
                          if (Object.keys(selectionState).length === 0) {
                            openModal({
                              type: 'supervision',
                              asEmptyQuote: true,
                            })
                          } else {
                            openModal({ type: 'quote', selectionState })
                          }
                        }}
                      >
                        Quote
                      </button>{' '}
                      or{' '}
                      <button
                        type="button"
                        className={styles.extraButton}
                        onClick={() => {
                          openModal({ type: 'supervision' })
                        }}
                      >
                        Supervision
                      </button>{' '}
                      request.
                    </p>
                  </form>
                )
              }}
            </Formik>
          )}
        </div>
      </div>
    </Layout>
  )
}

export default Login
